import React, { useState, useEffect, useMemo } from 'react';
import InputTypeSwitch from './InputTypeSwitch';
import IconButton from '../../buttons/IconButton';
import { FieldArray, useFormikContext } from 'formik';
import { getMaxLengthArray } from '../utility/getBeforeAfterData';
import renderValue from '../utility/renderValue';
import classNames from '../../../utility/classNames';
import Tooltip from '../../Tooltip';
import { checkCondition } from '../utility/filterSchema';
import { FaPlus } from 'react-icons/fa';

// This component is used to render a table with a single row for each item in the array, with a button to add new items. Schema for the table should be of length two
export default function BeforeAfterTableInline({
  name,
  condition,
  itemName,
  tableSchema,
  externalData = {},
  sectionHeaders,
  label,
  description,
  tooltipHeading,
  tooltipFormula,
}) {
  // Formik populates FieldArray with the array from initialData, so we can access it here through context
  const { values: afterValues, setValues } = useFormikContext();
  const [editIndex, setEditIndex] = useState(null);
  const beforeDataArray = externalData[name] || [];
  const afterDataArray = afterValues[name];

  useEffect(() => {
    //we want to modify the afterDataArray so the items are in the same order as the beforeDataArray, and the items that are not in the beforeDataArray are added at the end

    if (beforeDataArray.length === 0) {
      return;
    }

    const newAfterDataArray = [];
    // first match the items that are in both arrays and not marked as deleted
    beforeDataArray.forEach((beforeItem) => {
      const afterItem = afterDataArray.find(
        (item) => {
          return (
            item[tableSchema[0].name]?.trim() ===
            beforeItem[tableSchema[0].name]?.trim()
          );
        } // we assume the first field is the unique identifier
      );
      if (afterItem) {
        newAfterDataArray.push(afterItem);
      }
    });

    // then match the items that are in both arrays and are marked as deleted in the afterDataArray removed for now not sure if we need this
    // beforeDataArray.forEach((beforeItem) => {
    //   const afterItem = afterDataArray.find(
    //     (item) => {
    //       return (
    //         item[tableSchema[0].name]?.trim() ===
    //           beforeItem[tableSchema[0].name]?.trim() &&
    //         item[tableSchema[1].name] === 'DELETED'
    //       );
    //     } // we assume the first field is the unique identifier
    //   );
    //   if (afterItem) {
    //     newAfterDataArray.push(afterItem);
    //   }
    // });
    // then add the items that are in the beforeDataArray but not in the afterDataArray
    beforeDataArray.forEach((beforeItem) => {
      if (
        !newAfterDataArray.find(
          (item) =>
            item[tableSchema[0].name] === beforeItem[tableSchema[0].name]
        )
      ) {
        newAfterDataArray.push({
          [tableSchema[0].name]: beforeItem[tableSchema[0].name],
          [tableSchema[1].name]: beforeItem[tableSchema[1].name],
        });
      }
    });
    // then add the items that are in the afterDataArray but not in the beforeDataArray and not marked as deleted
    afterDataArray.forEach((afterItem) => {
      if (
        !newAfterDataArray.includes(afterItem) &&
        afterItem[tableSchema[1].name] !== 'DELETED'
      ) {
        newAfterDataArray.push(afterItem);
      }
    });

    setValues({ ...afterValues, [name]: newAfterDataArray });
  }, []);

  // In case beforeDataArray and afterDataArray have different numbers of items,
  // we need an empty array to iterate over so all items from both data sets are displayed
  const emptyArrayMaxLength = getMaxLengthArray(
    beforeDataArray,
    afterDataArray
  );

  const emptyItem = useMemo(
    () =>
      tableSchema.reduce((obj, field) => ({ ...obj, [field.name]: '' }), {}),
    [tableSchema]
  );
  return (
    <FieldArray
      name={name}
      render={(arrayHelpers) => (
        <div className="w-full sm:grid sm:grid-cols-[180px_2fr_1fr] -mt-2">
          {label && (
            <div className="col-span-3 mt-2 mb-2">
              <span className="text-md text-neutral-900 font-medium">
                {label}{' '}
                {description && (
                  <Tooltip
                    text={description}
                    heading={tooltipHeading}
                    formula={tooltipFormula}
                    classes="whitespace-normal"
                    type="info"
                  />
                )}
              </span>
            </div>
          )}
          {emptyArrayMaxLength.map((_, index) => {
            const cellClasses =
              'px-0 bg-white h-full flex items-center py-0 whitespace-nowrap';
            const beforeItem = beforeDataArray[index];

            const field = tableSchema[1];
            //render before value only if condition is not present or if condition is present and it is true, using the original / 'before' data
            const shouldShowBeforeValue =
              !condition || checkCondition(condition, externalData);
            const beforeValue =
              beforeItem && shouldShowBeforeValue
                ? renderValue(field, { ...externalData, ...beforeItem })
                : null;
            const beforeName =
              beforeItem && shouldShowBeforeValue
                ? renderValue(tableSchema[0], beforeItem)
                : null;

            const afterName = afterDataArray?.[index]?.[tableSchema[0].name];
            const afterValue = afterDataArray?.[index]?.[tableSchema[1].name];
            // if the item is marked as deleted, we don't want to render it
            const deleted = afterValue === 'DELETED';

            return (
              <React.Fragment key={`${tableSchema[0].name}-${index}`}>
                <React.Fragment key={field.name}>
                  <div className={classNames(cellClasses, 'mr-2 py-2 ')}>
                    {(beforeName || index !== editIndex) && (
                      <span
                        className={classNames(
                          'font-normal text-md',
                          deleted ? 'text-neutral-500' : 'text-neutral-900'
                        )}
                      >
                        {beforeName || afterName || `${itemName} ${index + 1}`}
                        {field.required && <span title="required">*</span>}{' '}
                        {field.description && (
                          <Tooltip
                            text={field.description}
                            heading={field.tooltipHeading}
                            formula={field.tooltipFormula}
                            classes="whitespace-normal"
                            type="info"
                          />
                        )}
                      </span>
                    )}
                    {!beforeName && index === editIndex && (
                      <>
                        <InputTypeSwitch
                          elem={{
                            ...tableSchema[0],
                            name: `${name}.${index}.${tableSchema[0].name}`, // this allows Formik to tell apart the same field in different array items
                            layout: 'tableNoLabel',
                            placeholder: `${itemName} ${index + 1}`,
                            //if there is no name, set the prevalue to the index
                            preValue: afterName
                              ? undefined
                              : `${itemName} ${index + 1}`,
                            emphasize: true,
                          }}
                          placeholder="Name"
                          externalData={externalData}
                        />
                        {field.required && (
                          <span className="text-warning inline">*</span>
                        )}
                      </>
                    )}
                  </div>
                  <div
                    className={classNames(
                      'sm:hidden text-base font-medium text-neutral-400 uppercase tracking-wider py-2',
                      cellClasses
                    )}
                  >
                    {Array.isArray(sectionHeaders[1]) ? (
                      <>
                        <span className="inline-block whitespace-nowrap">
                          {sectionHeaders[1][0]} &#47;
                        </span>
                        <span className="inline-block">
                          &nbsp;{sectionHeaders[1][1]}:
                        </span>
                      </>
                    ) : (
                      `${sectionHeaders[1]}:`
                    )}
                  </div>
                  <div
                    className={classNames(cellClasses, 'py-2 sm:col-span-1')}
                  >
                    {!deleted && (
                      <InputTypeSwitch
                        elem={{
                          ...field,
                          name: `${name}.${index}.${field.name}`, // this allows Formik to tell apart the same field in different array items
                          layout: 'tableNoLabel',
                        }}
                        externalData={externalData}
                      />
                    )}
                    {deleted && (
                      <div className="flex justify-start items-center gap-1 text-gray-500">
                        {/* <p className="text-base font-semibold tracking-wider">(deleted)</p> */}
                        <button
                          type="button"
                          onClick={() => {
                            arrayHelpers.replace(index, {
                              [tableSchema[0].name]: beforeName,
                              [tableSchema[1].name]:
                                beforeItem[tableSchema[1].name],
                            });
                          }}
                        >
                          <FaPlus className="text-base focus:text-gray-800 hover:text-gray-800" />
                        </button>
                      </div>
                    )}
                    {!deleted && (
                      <div className="pl-2">
                        <IconButton
                          icon="delete"
                          style="modal"
                          classes="focus:outline-none focus:ring-1 focus:ring-warning hover:text-warning focus:text-warning"
                          iconClasses="h-4 w-4"
                          handleClick={() => {
                            if (!beforeName && !beforeValue) {
                              arrayHelpers.remove(index);
                              if (beforeDataArray.length > index) {
                                beforeDataArray.splice(index, 1);
                              }
                            } else {
                              // we need to keep the item in the array, but mark it as deleted because we need to keep the order
                              arrayHelpers.replace(index, {
                                [tableSchema[0].name]: beforeName,
                                [tableSchema[1].name]: 'DELETED',
                              });
                            }
                          }}
                        />

                        {!beforeValue && !beforeName && (
                          <>
                            {editIndex === index && (
                              <IconButton
                                icon="save"
                                style="modal"
                                iconClasses="h-[17px] w-[17px]"
                                handleClick={() => setEditIndex(null)}
                              />
                            )}
                            {editIndex !== index && (
                              <IconButton
                                icon="edit"
                                style="modal"
                                iconClasses="h-4 w-4"
                                handleClick={() => setEditIndex(index)}
                              />
                            )}
                          </>
                        )}
                      </div>
                    )}
                  </div>

                  <div
                    className={classNames(
                      cellClasses,
                      'py-2 mr-2 sm:col-span-1'
                    )}
                  >
                    {beforeValue && (
                      <p className="sm:hidden text-base font-medium text-neutral-400 uppercase tracking-wider flex-grow">
                        {Array.isArray(sectionHeaders[2]) ? (
                          <>
                            <span className="inline-block whitespace-nowrap">
                              {sectionHeaders[2][0]} &#47;
                            </span>
                            <span className="inline-block">
                              &nbsp;{sectionHeaders[2][1]}:
                            </span>
                          </>
                        ) : (
                          `${sectionHeaders[2]}:`
                        )}
                      </p>
                    )}
                    <p className="sm:w-full sm:col-span-1 text-right text-md tracking-wider text-neutral-900">
                      {beforeValue && (
                        <span className="p-1 rounded border border-neutral-50">
                          {beforeValue}
                        </span>
                      )}
                    </p>
                  </div>
                </React.Fragment>
              </React.Fragment>
            );
          })}
          <div
            className={classNames(
              'col-span-3',
              emptyArrayMaxLength?.length && 'mt-10'
            )}
          >
            <IconButton
              text={`Add ${itemName || 'item'}`}
              icon="plus"
              iconPosition="left"
              classes={'min-w-[14rem]'}
              style="outline"
              handleClick={() => {
                arrayHelpers.push(emptyItem);
                setEditIndex(arrayHelpers.form.values[name].length);
              }}
              iconClasses="text-sm mb-[2px]"
            />
          </div>
        </div>
      )}
    />
  );
}
